import React, { useState, useEffect } from 'react';
import DonutChart from '../DonutChart';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const EprBiometricCharts = ({ filters }) => {
  const { accessToken } = useAuth();
  const [eprVsBiometricData, setEprVsBiometricData] = useState({ label: [], series: [] });
  const [biometricBreakdownData, setBiometricBreakdownData] = useState({ label: [], series: [] });
  const [scopeLeakageData, setScopeLeakageData] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [loadingLeakage, setLoadingLeakage] = useState(true);
  const [efficiencySummary, setEfficiencySummary] = useState({
    toolEffectiveness: '',
    totalLeakagePercentage: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const buildParams = (filters) => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });
    return params;
  };

  useEffect(() => {
    const fetchEprEfficiency = async () => {
      try {
        setLoadingCharts(true);
        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrl}/superAdminDashboard/getEfficiency`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: buildParams(filters),
        });

        if (response.data.data) {
          const epr = parseFloat(response.data.data.EprHoursPercentage.replace('%', '')) || 0;
          const biometric = parseFloat(response.data.data.BiometricHoursPercentage.replace('%', '')) || 0;

          setEprVsBiometricData({
            label: ['EPR Hours %', 'Biometric Hours %'],
            series: [epr, biometric],
          });

          setEfficiencySummary({
            toolEffectiveness: `${epr.toFixed(2)}%`,
            totalLeakagePercentage: `${biometric.toFixed(2)}%`,
          });
        }
      } catch (error) {
        console.error('Error fetching EPR vs Biometric data:', error);
      } finally {
        setLoadingCharts(false);
      }
    };

    const fetchBiometricBreakdown = async () => {
      try {
        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrl}/superAdminDashboard/getBiometricBreakdown`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: buildParams(filters),
        });

        if (response.data && response.data.success) {
          const biometricBreakdown = response.data.data.biometricBreakdown;
          const breakdown = response.data.data;

          const present = parseFloat(breakdown.presentPercentage.replace('%', '')) || 0;
          const absent = parseFloat(breakdown.AbsentPercentage.replace('%', '')) || 0;

          setBiometricBreakdownData({
            label: ['Present', 'Absent'],
            series: [present, absent],
          });
        }
      } catch (error) {
        console.error('Error fetching Biometric Breakdown data:', error);
      }
    };

    if (filters) {
      fetchEprEfficiency();
      fetchBiometricBreakdown();
    }
  }, [accessToken, filters]);

  useEffect(() => {
    const fetchScopeLeakage = async () => {
      try {
        setLoadingLeakage(true);
        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrl}/superAdminDashboard/getScopeWiseLeakage`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: buildParams(filters),
        });

        if (response.data && response.data.success) {
          const transformedData = response.data.data.map((item) => ({
            client: item.clientName,
            scope: item.scopeName,
            percentage: parseFloat(item.leakagePercentage.replace('%', '')),
          }));
          setScopeLeakageData(transformedData);
        }
      } catch (error) {
        console.error('Error fetching scope leakage list:', error);
      } finally {
        setLoadingLeakage(false);
      }
    };

    if (filters) {
      fetchScopeLeakage();
    }
  }, [accessToken, filters]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = scopeLeakageData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(scopeLeakageData.length / recordsPerPage);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
      {/* EPR vs Biometric */}
      <div className="bg-white p-6 rounded-xl shadow-md flex flex-col justify-between h-full">
        <h3 className="text-xl font-semibold mb-4 text-center">EPR vs Biometric</h3>
        {loadingCharts ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            <DonutChart data={eprVsBiometricData} />
            <div className="mt-6 text-sm text-gray-700 space-y-2 text-center">
              {eprVsBiometricData.label.map((label, index) => (
                <p key={index}>
                  <strong>{label}:</strong> {eprVsBiometricData.series[index]}%
                </p>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Biometric Breakdown */}
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="text-xl font-semibold mb-4 text-center">Biometric Breakdown</h3>
        {loadingCharts ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            <DonutChart data={biometricBreakdownData} />
            <div className="mt-6 text-sm text-gray-700 space-y-2 text-center">
              {biometricBreakdownData.label.map((label, index) => (
                <p key={index}>
                  <strong>{label}:</strong> {biometricBreakdownData.series[index]}%
                </p>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Scope-wise Leakage Table */}
      <div className="bg-gray-50 p-6 rounded-xl shadow-md relative col-span-1 lg:col-span-3">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Scope-wise Leakage Details</h3>
        </div>

        {loadingLeakage ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            <table className="min-w-full table-auto text-sm text-left border">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 border">#</th>
                  <th className="px-4 py-2 border">Client</th>
                  <th className="px-4 py-2 border">Scope</th>
                  <th className="px-4 py-2 border">Leakage %</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((item, index) => (
                  <tr key={index} className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-2 border">{indexOfFirstRecord + index + 1}</td>
                    <td className="px-4 py-2 border">{item.client}</td>
                    <td className="px-4 py-2 border">{item.scope}</td>
                    <td
                      className={`px-4 py-2 border font-semibold ${
                        item.percentage >= 8 ? 'text-red-600' : item.percentage >= 5 ? 'text-yellow-600' : 'text-green-600'
                      }`}
                    >
                      {item.percentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div className="flex justify-center items-center space-x-2 mt-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 border rounded ${currentPage === i + 1 ? 'bg-[#1A2B5C] text-white' : ''}`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EprBiometricCharts;
